import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";

type APIDeploymentIndex = { [folder: string]: number };

export type Deployment = {
  folder: string;
  url: string;
  lastModification: Date;
  jiraId?: string;
  jiraUrl?: string;
};

@Injectable({
  providedIn: "root",
})
export class TestToolService {
  private deploymentsSource: BehaviorSubject<Deployment[]> =
    new BehaviorSubject<Deployment[]>([]);

  public deployments$: Observable<Deployment[]> =
    this.deploymentsSource.asObservable();

  public latestDeployments$: Observable<Deployment[]> = this.deployments$.pipe(
    map((deployments) => this.getLatest(deployments)),
  );

  public currentDeployment$: Observable<Deployment | undefined> =
    this.deployments$.pipe(
      map((deployments) =>
        deployments.find((deployment) =>
          window.location.hostname.includes(deployment.folder),
        ),
      ),
    );

  constructor(private http: HttpClient) {
    // Only relevant if hosted on a dynamic deployment,
    // otherwise the deployment-index.json is not present.
    if (window.location.hostname.includes("siptrunk-test.nfon.com")) {
      this.http
        .get<APIDeploymentIndex>("/deployment-index.json")
        .subscribe((deploymentIndex: APIDeploymentIndex) => {
          this.deploymentsSource.next(
            this.mapIndexToDeployment(deploymentIndex),
          );
        });
    }
  }

  public switchToDeployment(target: Deployment): void {
    window.location.href = target.url;
  }

  private getLatest(deployments: Deployment[]): Deployment[] {
    return (
      deployments
        // In last 24 hours.
        .filter(
          (deployment) =>
            deployment.lastModification.getTime() >
            Date.now() - 24 * 60 * 60 * 1000,
        )
        // Most recent first.
        .sort(
          (a, b) => b.lastModification.getTime() - a.lastModification.getTime(),
        )
        // Limit maximum.
        .slice(0, 5)
    );
  }

  // Extends the deployment index information into an object with more information for further use.
  // Creates link URLs and detects Jira IDs from the folder name.
  // Also parses the timestamp into JS Date.
  private mapIndexToDeployment(
    deploymentIndex: APIDeploymentIndex,
  ): Deployment[] {
    return Object.entries(deploymentIndex).map(([folder, timestamp]) => {
      const url: string = `https://${folder}.siptrunk-test.nfon.com`;
      const lastModification: Date = new Date(timestamp * 1000);
      const jiraIdMatch: RegExpMatchArray | null = folder.match(
        /[A-Z][A-Z0-9]+-[0-9]+/i,
      );

      const result: Deployment = {
        folder,
        url,
        lastModification,
      };

      if (jiraIdMatch) {
        result.jiraId = jiraIdMatch[0].toUpperCase();
        result.jiraUrl = `https://jira.nfon.net/browse/${result.jiraId}`;
      }

      return result;
    });
  }
}
